import { z } from 'zod';
import {
  DIAGNOSTIC_LIMIT,
  ISO_DATETIME,
  STORAGE_KEY_LIMIT,
  TITLE_LIMIT,
  TLDR_LIMIT,
  URL_LIMIT,
  UUID,
  optionalCategoryKey,
  optionalLimitedString,
  requiredLimitedString,
} from './issues-schema-utils.js';
import {
  IssueSeveritySchema,
  IssueSourceKindSchema,
  type IssueCategorySummary,
  type IssueSeverity,
  type IssueSourceKind,
  type IssueStatus,
} from './issue-core.js';
import type { FailureDiagnosticEvidence } from './issue-evidence.js';
import { AgentProductMapLinkSchema } from './issues-manual.js';
import type { Pagination } from './pagination.js';
export * from './issue-core.js';
export * from './issue-evidence.js';
export * from './issues-manual.js';
export * from './issues-diagnostic.js';
export type { Pagination } from './pagination.js';

/**
 * A single network request captured from a Playwright trace.
 * Bodies are truncated to 100KB.
 */
export interface NetworkRequestEntry {
  url: string;
  method: string;
  status: number | null;
  durationMs: number | null;
  requestBody: string | null;
  responseBody: string | null;
  requestHeaders: Record<string, string> | null;
  responseHeaders: Record<string, string> | null;
  contentType: string | null;
}

/**
 * A single console log entry captured from a Playwright trace.
 */
export interface ConsoleLogEntry {
  type: 'log' | 'warning' | 'error' | 'info' | 'debug';
  text: string;
  timestamp: number | null;
  location: string | null;
}

/**
 * Category of failure root cause — add new values to this array (type is derived).
 *
 * `app_changed` is distinct from both `software_bug` and `test_underspecified`: the
 * application's behavior/labels/flow changed versus the last passing run while the
 * authored test stayed the same, and the agent cannot verify from inside the sandbox
 * whether the change was intentional. The recommendation pattern is "X appears to have
 * changed to Y; update the test to match if intentional." Reserve `software_bug` for
 * behavior deltas that are clearly defects (error page, 5xx, broken layout).
 */
export const FAILURE_DIAGNOSTIC_CATEGORIES = [
  'software_bug',
  'dependency_issue',
  'agent_confusion',
  'platform_error',
  'test_underspecified',
  'data_collision',
  'app_changed',
  'test_defect',
] as const;
export type FailureDiagnosticCategory = (typeof FAILURE_DIAGNOSTIC_CATEGORIES)[number];

/**
 * Categories a native (natural-language Cofactor flow) diagnosis may choose.
 * `test_underspecified` and `agent_confusion` only make sense when an AI agent
 * interpreted authored natural language; `test_defect` belongs to customer code.
 */
export const NATIVE_DIAGNOSTIC_CATEGORIES = [
  'software_bug',
  'dependency_issue',
  'agent_confusion',
  'platform_error',
  'test_underspecified',
  'data_collision',
  'app_changed',
] as const satisfies readonly FailureDiagnosticCategory[];

/**
 * Categories an external-test (customer Playwright/Selenium/… code) diagnosis may
 * choose, in the order the external prompt's decision procedure checks them.
 * `test_defect` means the customer's test code is wrong: a locator, helper,
 * assertion, wait, or a setup step in the test repository that does not produce
 * the state the test asserts on.
 */
export const EXTERNAL_DIAGNOSTIC_CATEGORIES = [
  'platform_error',
  'data_collision',
  'software_bug',
  'app_changed',
  'dependency_issue',
  'test_defect',
] as const satisfies readonly FailureDiagnosticCategory[];

/**
 * One key moment in the diagnostic's root-cause timeline — an ordered "what to
 * watch" event the QA tester can jump to in the run video. Surfaced both as
 * markers on the video's diagnostic-timeline track and as the timestamped
 * key-evidence list at the top of the run Summary.
 */
export interface RootCauseTimelineEvent {
  /**
   * Milliseconds from the start of the video recording — the same scale as the
   * run's `videoTimelineJson` entries and `FailureDiagnosticEvidence.timestamp`
   * (the `relativeTimestampMs` column in the diagnostic workspace's
   * `timeline/actions.jsonl`, NOT the absolute wall-clock `timestampMs`).
   */
  timestampMs: number;
  /** Short marker/row label (≤8 words). */
  title: string;
  /** Optional one-line elaboration shown on hover / under the row. */
  detail?: string;
  /** Visual weight of the marker. Defaults to `info` when omitted. */
  severity?: 'info' | 'warning' | 'critical';
}

/** Structured diagnostic analysis produced by the failure diagnostic agent */
export interface FailureDiagnostic {
  /**
   * Provenance of this record. Absent (or `'agent'`) = produced by the
   * failure-diagnostic agent — a real root-cause analysis. `'manual'` = evidence
   * attached to a manually-filed issue (via `cofactor issue create`); the UI
   * renders these as "Attached evidence" rather than a completed root-cause
   * analysis, since `category`/`recommendation` are not asserted by the filer.
   * `'diagnostic'` = evidence attached by the failure-diagnostic agent's
   * `file_issue` tool — category IS asserted (the diagnosis root cause), so the
   * UI shows the category badge, but the full root-cause narrative lives on the
   * linked `failure_diagnostics` row rather than here.
   */
  source?: 'agent' | 'manual' | 'diagnostic';
  category: FailureDiagnosticCategory;
  /** One-sentence thesis — the QA tester reads only this 80% of the time. */
  title: string;
  /**
   * Legacy freeform Markdown narrative (kept for back-compat with already-persisted rows).
   * The current prompt emits `failurePoints` bullets instead; new diagnostics may omit `analysis`.
   */
  analysis?: string;
  /** 2–4 concise bullets describing the failure (current report shape). */
  failurePoints?: string[];
  evidence: FailureDiagnosticEvidence[];
  smokingGun?: {
    type: string;
    description: string;
    detail?: string;
  };
  recommendation: string;
  comparisonWithHistory?: string;
  /**
   * The agent's affirmation that it walked backwards and confirmed the effect of the
   * upstream cache-fallback / cache-replay / variable-capture steps before blaming a
   * cause outside the flow. Required-by-nudge (not schema) on external-cause verdicts
   * (`app_changed` / `software_bug` / `dependency_issue` / `platform_error`). Persisted
   * for audit + telemetry on the upstream-walk-back rate. Absent on non-external verdicts
   * and on diagnoses that predate the field.
   */
  upstreamStepsVerified?: string;
  /** Things the agent could not determine from available evidence (display-only for now). */
  openQuestions?: string[];
  /**
   * Whether the agent materialized a green-baseline comparison workspace before this
   * verdict (v2 `materialize_run` tool). `false` on an `app_changed` verdict means the
   * comparison gate was bypassed (second submission or step-cap salvage) — treat the
   * verdict as low-trust. Absent on diagnoses that predate the field.
   */
  comparisonPerformed?: boolean;
  /** Explicit limitations when gates were bypassed or findings were salvaged. */
  qualityWarnings?: string[];
  completedAt: string;
  agentRunId?: string;
  /**
   * Set when the agent drafted a flow-fix proposal for this failure (via the
   * diagnostic `propose_flow_fix` tool). The run-detail summary uses these to
   * describe the suggested change and deep-link to its proposal review. Absent
   * when the agent proposed no flow change.
   */
  proposalId?: string;
  /** One-line description of the proposed change (the proposal's title). */
  proposalSummary?: string;
  /**
   * Optional ordered root-cause timeline — key moments to watch in the run
   * video, each anchored to a video-relative timestamp so the UI can mark them
   * on the player and let the tester jump to them. Absent on older diagnoses.
   */
  rootCauseTimeline?: RootCauseTimelineEvent[];
  /**
   * Present ONLY on a repeat-failure verdict: the native triage phase that runs
   * when an already-red flow fails again, or an external test whose failure
   * signature matches its previous failure. The verdict inherits the prior
   * diagnosis's `category`/`recommendation`, so this is what distinguishes
   * "confirmed the established root cause still applies" from a fresh
   * root-cause analysis. Absent on every full-agent diagnosis.
   */
  triage?: {
    /** Plain-language justification for treating this as a repeat failure. */
    reasoning: string;
    /**
     * External tests only: the agent diagnosis an external test's repeat
     * failure inherited, by signature, without running the agent. Always the
     * original analysis — a repeat of a repeat links to it, not to the copy.
     * Absent on native triage verdicts. `runCreatedAt` (ISO) is when the
     * original run started, for "Same failure as <date>".
     */
    repeatOf?: { runId: string; itemId: string; diagnosticId: string; runCreatedAt: string };
    /**
     * The open issue this run was recorded against — absent when the flow had
     * none describing this failure, which is the common case.
     */
    matchedIssueId?: string;
    /** Shortcode of that issue, for display without a join. */
    matchedIssueShortcode?: string;
  };

  /** A narrowly guarded suite-result correction applied after this diagnosis. */
  outcomeCorrection?: {
    from: 'failed';
    to: 'success';
    correctedAt: string;
  };
}

export interface IssueOccurrence {
  id: string;
  issueId: string;
  orgId: string;
  propertyId: string;
  canonicalPageId: string | null;
  credentialId: string | null;
  sourceKind: IssueSourceKind;
  sourceId: string;
  detectedAt: string;
  primaryUrl: string;
  tldr: string | null;
  summary: string | null;
  errorDetails: string | null;
  screenshotStorageKey: string | null;
  screenshotCaption: string | null;
  traceStorageKey: string | null;
  videoStorageKey: string | null;
  /** Action timeline for video overlay synchronization */
  videoTimelineJson: unknown | null;
  /** Condensed "fast" video variant (preferred for playback when present). */
  fastVideoStorageKey: string | null;
  /** Remapped timeline + cut metadata for the fast video variant. */
  fastVideoTimelineJson: unknown | null;
  consoleLogExcerpt: string | null;
  networkFailureUrl: string | null;
  networkFailureStatus: number | null;
  ticketReproSteps: string | null;
  ticketUserFacingSummary: string | null;
  ticketDiagnosticsAnalysis: string | null;
  /** What the test expected to observe. Presence signals the simplified three-section issue layout. */
  ticketExpectedBehavior: string | null;
  dedupReasoning: string | null;
  /** Status of async failure diagnostic analysis */
  diagnosticStatus: string | null;
  /** Structured diagnostic analysis from the failure diagnostic agent */
  diagnosticJson: FailureDiagnostic | null;
  /** Resolved workflow IDs when sourceKind is 'workflow_test' */
  workflowSourceInfo?: { workflowId: string; workflowRunId: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface IssueAssignee {
  id: string;
  displayName: string | null;
  primaryEmail: string | null;
}

export interface IssueSummary {
  id: string;
  orgId: string;
  propertyId: string;
  credentialId: string | null;
  title: string;
  tldr: string | null;
  severity: IssueSeverity;
  /** The flow this issue is filed against (workflow-test sources); null otherwise. */
  workflowId: string | null;
  category: IssueCategorySummary;
  confidence: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrenceCount: number;
  status: IssueStatus;
  statusUpdatedAt: string;
  closedAt: string | null;
  /** The canonical issue this one was closed as a duplicate of; null when it isn't a duplicate. */
  duplicateOfIssueId: string | null;
  isArchived: boolean;
  assignedToId: string | null;
  assignedTo: IssueAssignee | null;
  createdAt: string;
  updatedAt: string;
  latestOccurrence: IssueOccurrence | null;
  tags?: Array<{ id: string; name: string; color: string }>;
}

export interface IssueDetail extends IssueSummary {
  occurrences: IssueOccurrence[];
}

export interface PaginatedIssuesResponse {
  data: IssueSummary[];
  pagination: Pagination;
}

export interface IssueDetailResponse {
  issue: IssueSummary;
  occurrences: IssueOccurrence[];
  occurrencesPagination: Pagination;
  commentCount: number;
}

export interface PaginatedIssueOccurrencesResponse {
  data: IssueOccurrence[];
  pagination: Pagination;
}

export interface IssueCommentAuthor {
  id: string;
  displayName: string | null;
  primaryEmail: string | null;
}

export interface IssueComment {
  id: string;
  issueId: string;
  orgId: string;
  propertyId: string;
  authorId: string;
  author: IssueCommentAuthor | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedIssueCommentsResponse {
  data: IssueComment[];
  pagination: Pagination;
}

export const RecordIssueOccurrencePayloadSchema = z.object({
  orgId: UUID,
  propertyId: UUID,
  canonicalPageId: UUID.optional()
    .nullable()
    .transform((value) => value ?? null),
  credentialId: UUID.optional()
    .nullable()
    .transform((value) => value ?? null),
  issueTitle: requiredLimitedString('Issue title', TITLE_LIMIT),
  issueSeverity: IssueSeveritySchema,
  issueCategoryId: UUID.optional()
    .nullable()
    .transform((value) => value ?? null),
  issueCategoryKey: optionalCategoryKey,
  issueConfidence: z
    .number()
    .int()
    .min(0)
    .max(100)
    .optional()
    .nullable()
    .transform((value) => {
      if (value === null || value === undefined) return null;
      return value;
    }),
  detectedAt: ISO_DATETIME,
  sourceKind: IssueSourceKindSchema,
  sourceId: requiredLimitedString('Source identifier', STORAGE_KEY_LIMIT),
  primaryUrl: requiredLimitedString('Primary URL', URL_LIMIT),
  summary: requiredLimitedString('Summary', DIAGNOSTIC_LIMIT),
  tldr: requiredLimitedString('TLDR', TLDR_LIMIT),
  screenshotStorageKey: requiredLimitedString('Screenshot storage key', STORAGE_KEY_LIMIT),
  screenshotCaption: optionalLimitedString(DIAGNOSTIC_LIMIT),
  traceStorageKey: optionalLimitedString(STORAGE_KEY_LIMIT),
  videoStorageKey: optionalLimitedString(STORAGE_KEY_LIMIT),
  /** Action timeline for video overlay synchronization */
  videoTimelineJson: z.unknown().optional().nullable(),
  consoleLogExcerpt: optionalLimitedString(DIAGNOSTIC_LIMIT),
  networkFailureUrl: optionalLimitedString(URL_LIMIT),
  networkFailureStatus: z
    .number()
    .int()
    .optional()
    .nullable()
    .transform((value) => value ?? null),
  /** Structured network requests captured from Playwright trace */
  networkRequestsJson: z.array(z.unknown()).optional().nullable(),
  /** Structured console logs captured from Playwright trace */
  consoleLogsJson: z.array(z.unknown()).optional().nullable(),
  ticketReproSteps: optionalLimitedString(DIAGNOSTIC_LIMIT),
  ticketUserFacingSummary: requiredLimitedString('Ticket summary', DIAGNOSTIC_LIMIT),
  ticketDiagnosticsAnalysis: requiredLimitedString('Ticket diagnostics analysis', DIAGNOSTIC_LIMIT),
  /** The capability (and rule) the filing agent says the bug breaks; see `AgentProductMapLinkFields`. */
  productMapLink: AgentProductMapLinkSchema.optional(),
});

export type RecordIssueOccurrencePayload = z.infer<typeof RecordIssueOccurrencePayloadSchema>;
